import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import WbgBorg from "../assets/images/WBGBorg.png"; // image shown left on large screens, above on small
import WbgBorgThinking from "../assets/images/WBGBorgThinking.png";

const API_BASE = "https://1y47599kr5.execute-api.us-west-1.amazonaws.com/prod/";
const API_KEY = "qq3LUbhaEc16v6kuQjUpA8bpnb8VTtu0al8anv4c";
const IP_HEADER_VALUE = "test";

const LOADING_LINES = [
  "Hold on… gotta check if I still have chips left before I answer this one.",
  "Running this question through my lucky slot machine brain…",
  "Thinking harder than I do when I get pocket 7s.",
  "Hang tight, I just doubled down on this answer.",
  "One sec — I’m calculating odds like it’s the main event.",
  "Waiting for my answer to clear the casino cage.",
  "Processing… and maybe ordering a comped drink.",
  "If this takes too long, assume I hit a jackpot mid-sentence.",
  "Don’t worry, I’m bluffing about how long this will take.",
  "Checking the math… and yeah, it’s probably wrong.",
  "Loading… but I might’ve wandered off to a craps table.",
  "Almost there — my dealer’s just shuffling the data.",
  "The AI’s hot right now… riding the streak!",
  "Waiting on my brain’s pit boss for approval.",
  "Tossing a few coins in the slot of wisdom… jackpot pending.",
  "Vegas Matt’s filming, so I gotta look smart for a sec.",
  "This question’s tougher than a bad beat on the river.",
  "Consulting my spreadsheet of losses… I mean data.",
  "Still thinking… but I’m charging $25 a minute for this.",
  "Running simulations… and losing them all.",
  "Even W2Jesus can’t save how slow this thought is loading.",
  "Mic check, mic check… okay, back to pretending I know math.",
  "One sec — the answer’s dancing somewhere on the casino floor.",
  "Consulting my AI crystal ball… results unclear, but I feel lucky.",
  "Cooling off before I tilt over this question.",
  "Pulling the lever of logic… come on, triple 7s!",
  "Taking a break outside the casino… mentally and financially.",
  "Answer loading… if it wins, I’m taking all the credit.",
];

const THINKING_MSG = "ChatWBG is thinking. The service is very busy so it may take up to 60 seconds.";

const SUGGESTIONS = [
  "How much are we up or down year‑to‑date?",
  "What was our best day this year, and how big was it?",
  "Show the running total by trip location.",
  "Do days with a successful question of the day perform better?",
  "Which slot gave the biggest bonus, and how much was it?",
  "How many handpays did we hit, and on which days?",
  "Who’s ahead on side bets, and by how much?",
  "What were the biggest swings on table games?",
  "When did we hit a perfect ticket?",
  "What’s our high‑water mark this year versus today?",
  "Show GEB daily totals and the running tally.",
  "Compare long‑form versus short‑form totals over time.",
  "Which trips had the most participants?",
  "Any patterns on days with lots of bonuses?",
  "Which locations treated us best?",
  "When did we get back to even?"
];

export default function ChatWBG() {
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState("idle"); // idle | posting | polling | done | error
  const [requestId, setRequestId] = useState(null);
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [loadingIndex, setLoadingIndex] = useState(0);
  const [typedThinking, setTypedThinking] = useState("");
  const [showThinking, setShowThinking] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 600px)").matches;
  });

  const abortRef = useRef(null);
  const pollTimerRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);
  const isLoading = status === "posting" || status === "polling";

  const syncInputHeight = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const minHeight = isSmallScreen ? 96 : 56;
    const desktopMinHeight = 56;
    el.style.height = "auto";
    const nextHeight = Math.max(el.scrollHeight, minHeight);
    el.style.height = `${nextHeight}px`;
    // Match Ask button height: fixed on small screens (minHeight), dynamic on large screens
    const buttonEl = buttonRef.current;
    if (buttonEl) {
      if (isSmallScreen) {
        // On mobile, keep the button compact: use desktop single-line height (56px)
        buttonEl.style.height = `${desktopMinHeight}px`;
      } else {
        const h = el.offsetHeight; // includes borders
        buttonEl.style.height = `${h}px`;
      }
    }
  }, [isSmallScreen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(max-width: 600px)");
    const handleChange = (event) => setIsSmallScreen(event.matches);

    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else {
      media.addListener(handleChange);
    }

    setIsSmallScreen(media.matches);
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(syncInputHeight);
    } else {
      syncInputHeight();
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, [syncInputHeight]);

  useEffect(() => {
    syncInputHeight();
  }, [question, syncInputHeight]);

  // Rotate loading lines while posting/polling
  useEffect(() => {
    if (status === "posting" || status === "polling") {
      const t = window.setInterval(
        () => setLoadingIndex((i) => (i + 1) % LOADING_LINES.length),
        10000
      );
      return () => window.clearInterval(t);
    }
  }, [status]);

  useEffect(() => {
    // cleanup on unmount
    return () => {
      abortRef.current?.abort();
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const isThinking = status === "posting" || status === "polling";
    setShowThinking(isThinking);
    if (!isThinking) {
      setTypedThinking("");
      return;
    }
    // Set static text for glowing effect
    setTypedThinking(THINKING_MSG);
  }, [status]);

  useEffect(() => {
    if (!isLoading) {
      const t = window.setInterval(() => {
        setSuggestionIndex((i) => (i + 1) % SUGGESTIONS.length);
      }, 4000);
      return () => window.clearInterval(t);
    }
  }, [isLoading]);

  const currentLoadingLine = useMemo(
    () => LOADING_LINES[loadingIndex],
    [loadingIndex]
  );

  const submitQuestion = async (e) => {
    e?.preventDefault();
    setAnswer("");
    setError("");

    const q = question.trim();
    if (!q) return;

    // Clear the input so the overlay text doesn't overlap
    setQuestion("");
    inputRef.current && inputRef.current.blur();
    setStatus("posting");
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const res = await fetch(API_BASE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
          ip: IP_HEADER_VALUE,
        },
        body: JSON.stringify({ question: q }),
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`POST failed: ${res.status} ${res.statusText} — ${text}`);
      }

      const data = await res.json();
      const rid = data?.requestId || data?.requestID || data?.id;
      if (!rid) throw new Error("Server did not return a requestId.");

      setRequestId(rid);
      setStatus("polling");
      startPolling(rid);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError(
        "All in… and out of chips. You’ve hit today’s 5 request daily limit or ChatWBG is taking a break! Come back later."
      );
    }
  };

  const startPolling = (rid) => {
    if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);

    const doPoll = async () => {
      try {
        const url = `${API_BASE}?requestId=${encodeURIComponent(rid)}`;
        const res = await fetch(url, {
          method: "GET",
          headers: { ip: IP_HEADER_VALUE },
          signal: abortRef.current?.signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(`GET failed: ${res.status} ${res.statusText} — ${text}`);
        }

        const data = await res.json();
        const s = data?.status;

        if (s === "done") {
          let textOut = "";
          try {
            const parsed =
              typeof data.result === "string" ? JSON.parse(data.result) : data.result;
            textOut = parsed?.answer ?? JSON.stringify(parsed ?? {});
          } catch {
            textOut = String(data?.result ?? "");
          }
          setAnswer(textOut || "(No answer returned)");
          setStatus("done");
          if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        } else if (s === "error") {
          setStatus("error");
          const errMsg = data?.error || data?.message || "Unknown server error.";
          setError(
            `${errMsg} If this keeps happening, you may have hit daily limits per person or the service is busy.`
          );
          if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        } // else still processing
      } catch (err) {
        console.error("Polling error", err);
        setStatus("error");
        setError(
          "Couldn’t retrieve the result. You may have hit per-person daily limits or the service might be busy."
        );
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
      }
    };

    doPoll();
    pollTimerRef.current = window.setInterval(doPoll, 5000);
  };

  // Minimal component-scoped styles ONLY for what we need here
  const styles = {
    inputImageContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.75rem",
      margin: "1rem auto",
      maxWidth: "100%",
      width: "100%",
    },
    form: {
      flex: 1,
      width: "100%",
      display: "flex",
      gap: "0.5rem",
      position: "relative",
      alignItems: "stretch",
    },
    input: {
      flex: 1,
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: 8,
      border: "1px solid #ccc",
      fontSize: "1rem",
      lineHeight: 1.4,
      resize: "none",
      overflow: "hidden",
    },
    button: {
      background: "#76ace0",
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "0 1.25rem",
      fontWeight: 700,
      cursor: "pointer",
      opacity: !question.trim() || status === "posting" || status === "polling" ? 0.7 : 1,
      alignSelf: "stretch",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // height will be set dynamically
    },
    outputCard: {
      borderRadius: 8,
      padding: "1rem",
      maxWidth: "100%",
      margin: "1rem auto",
    },
    error: { color: "#df87a8", fontWeight: 600 },
  };

  return (
    <div className="App" style={{position: "relative", zIndex: 1}}>
      {/* Scoped styles for layout + glow text */}
      <style>{`
        .wbg-image { 
          width: 100%; 
          max-width: 100px; 
          height: auto; 
          object-fit: contain; 
          border-radius: 0; 
          box-shadow: none; 
        }
        .input-image-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin: 1rem auto;
          max-width: 900px;
          width: 100%;
        }
        @media (max-width: 600px) {
          .input-image-container {
            flex-direction: column;
            align-items: center;
          }
          .chatwbg-form .thinking-msg {
            position: static;
            margin-top: 12px;
            text-align: center;
            display: block;
            width: 100%;
            z-index: 0;
            animation: thinking-glow 2s ease-in-out infinite;
          }
          .chatwbg-form { flex-direction: column; }
          .chatwbg-form button { width: 100%; }
        }
        @media (min-width: 601px) {
          .chatwbg-form.is-loading {
            flex-wrap: wrap;
          }
          .chatwbg-form.is-loading .thinking-msg {
            position: static;
            width: 100%;
            display: flex;
          }
        }
        .wbg-glow {
          font-size: 1rem;
          font-weight: 800;
          line-height: 1.3;
          background: linear-gradient(90deg, #96e6ab, #76ace0, #df87a8, #f4d353, #96e6ab);
          background-size: 300% 100%;
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 12px rgba(118,172,224,0.45));
          animation: wbg-hue 8s linear infinite, wbg-pulse 2.2s ease-in-out infinite;
          text-align: center;
        }
        .wbg-glow.in-input {
          text-align: left;
        }
        @keyframes wbg-hue { 0% { background-position: 0% 50%; } 100% { background-position: 100% 50%; } }
        @keyframes wbg-pulse { 0%,100% { filter: drop-shadow(0 0 12px rgba(118,172,224,0.45)); } 50% { filter: drop-shadow(0 0 22px rgba(223,135,168,0.55)); } }

        /* Hide placeholder while loading so it doesn't overlap the overlay */
        .loading .chatwbg-input::placeholder { color: transparent; }
        .loading .chatwbg-input::-ms-input-placeholder { color: transparent; }
        .loading .chatwbg-input::-webkit-input-placeholder { color: transparent; }

        .thinking-msg {
          position: absolute;
          top: -28px;
          left: 0;
          right: 0;
          font-weight: 600;
          text-align: left;
          transition: opacity 0.25s ease;
          opacity: 0;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #76ace0;
          animation: thinking-glow 2s ease-in-out infinite;
        }
        @keyframes thinking-glow {
          0%, 100% {
            opacity: 0.4;
            text-shadow: 0 0 0px rgba(118,172,224,0), 0 0 0px rgba(118,172,224,0);
          }
          50% {
            opacity: 1;
            text-shadow: 0 0 10px rgba(118,172,224,0.8), 0 0 22px rgba(118,172,224,0.7);
          }
        }
        .thinking-spinner {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid rgba(0,0,0,0.2);
          border-top-color: #76ace0;
          animation: wbg-spin 0.8s linear infinite;
          flex: 0 0 auto;
          display: inline-block;
          vertical-align: middle;
          margin-right: 8px;
        }
        @keyframes wbg-spin { to { transform: rotate(360deg); } }
        .thinking-text { color: #0a3a5f; }
      `}</style>


      <div className="input-image-container" style={styles.inputImageContainer}>
        <img
          className="wbg-image"
          src={status === "posting" || status === "polling" ? WbgBorgThinking : WbgBorg}
          alt="WBG"
        />
        <form
          onSubmit={submitQuestion}
          style={styles.form}
          className={`chatwbg-form${isLoading ? " is-loading" : ""}`}
        >
          <div style={{ position: "relative", flex: 1 }} className={isLoading ? "loading" : ""}>
            <textarea
              ref={inputRef}
              className="chatwbg-input"
              style={{
                ...styles.input,
                ...(isLoading ? { color: 'transparent', caretColor: 'transparent' } : {}),
              }}
              placeholder={isLoading || !isLoading ? "" : "Type your question…"}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={isLoading}
              rows={1}
            />
            {(isLoading || (!isLoading && !question.trim())) && (
              <div className="wbg-glow in-input" style={{
                position: "absolute",
                left: 12,
                right: 12,
                top: isSmallScreen ? 12 : "50%",
                transform: isSmallScreen ? "none" : "translateY(-50%)",
                pointerEvents: "none",
                fontSize: styles.input.fontSize,
                whiteSpace: isSmallScreen ? "normal" : "nowrap",
                overflow: "hidden",
                textOverflow: isSmallScreen ? "clip" : "ellipsis",
                lineHeight: isSmallScreen ? 1.4 : undefined,
              }}>
                {isLoading ? currentLoadingLine : SUGGESTIONS[suggestionIndex]}
              </div>
            )}
          </div>
          <button
            style={styles.button}
            ref={buttonRef}
            disabled={!question.trim() || status === "posting" || status === "polling"}
          >
            {status === "posting" || status === "polling" ? "Asking WBG…" : "Ask"}
          </button>
          <div
            className="thinking-msg"
            style={{ opacity: showThinking ? 1 : 0 }}
            aria-live="polite"
          >
            {isLoading && <span className="thinking-spinner" aria-hidden></span>}
            <span className="thinking-text">{typedThinking}</span>
          </div>
        </form>
      </div>

      {/* Final answer / error display */}
      {status === "done" && (
        <div style={styles.outputCard}>
          <h3 style={{ margin: "0 0 8px 0" }}>Answer</h3>
          <div style={{ whiteSpace: "pre-wrap" }}>{answer}</div>
        </div>
      )}

      {status === "error" && (
        <div style={{ ...styles.outputCard, ...styles.error }}>
          {error ||
            "Something went wrong. You may have hit per-person daily limits or the service is busy."}
        </div>
      )}
    </div>
  );
}
